import React, { useEffect, useState } from 'react';
import { Box, SxProps, TextField, Typography } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { Button } from 'system/atoms';
import { useRecoilValue } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import {
  useGetOrganizationConfigByOrganizationIdQuery,
  useUpsertOrganizationConfigMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { Flags, useFlag } from 'services/flags';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { HtmlIframeViewer } from 'components/HtmlIframeViewer/HtmlIframeViewer';
import { ToastType, useToast } from 'services/toast';

export const DEFAULT_EMAIL_FOOTER_HTML =
  '<p style="color:#6b7280;font-size:12px;">Sent by our recruitment team.</p>';

const container: SxProps = { px: 0.5, py: 2 };
const actions: SxProps = { mt: 1, display: 'flex', gap: 1 };
const previewFrame: SxProps = {
  mt: 2,
  border: '1px solid',
  borderColor: 'customGrey.medium',
  borderRadius: 1,
  overflow: 'hidden',
  bgcolor: 'common.white'
};

type Props = {
  isEmailEnabled: boolean;
};

export const EmailFooterSetting: React.FC<Props> = ({ isEmailEnabled }) => {
  const isFlagEnabled = useFlag(Flags.HTML_EMAIL_FOOTER);
  const organization = useRecoilValue(organizationAtom);
  const [upsertOrganizationConfig, { loading }] =
    useUpsertOrganizationConfigMutation();
  const {
    data,
    loading: isConfigLoading,
    refetch
  } = useGetOrganizationConfigByOrganizationIdQuery({
    variables: { organizationId: organization?.id ?? '' },
    skip: !organization?.id
  });
  const { openModal, closeModal } = useModalV2();
  const { showToast } = useToast();

  // The query is keyed on the current org, so its result is the single source
  // of truth for the footer — avoids racing a separately-populated atom.
  const loadedFooterHtml =
    data?.getOrganizationConfigByOrganizationId?.items?.[0]?.emailFooterHtml;

  const [footerHtml, setFooterHtml] = useState(
    loadedFooterHtml ?? DEFAULT_EMAIL_FOOTER_HTML
  );
  const [hasLocalEdits, setHasLocalEdits] = useState(false);

  // Discard in-progress edits when the org changes so a draft from one org is
  // never adopted or saved into another.
  useEffect(() => {
    setHasLocalEdits(false);
  }, [organization?.id]);

  // Adopt the current org's persisted footer once its config resolves (from
  // cache or network), without clobbering the user's in-progress edits.
  useEffect(() => {
    if (!hasLocalEdits) {
      setFooterHtml(loadedFooterHtml ?? DEFAULT_EMAIL_FOOTER_HTML);
    }
  }, [loadedFooterHtml, hasLocalEdits]);

  if (!isEmailEnabled || !isFlagEnabled) {
    return null;
  }

  // Only allow saving once the current org's config has actually loaded. The
  // editor value comes from this query, so saving while it's still in flight
  // could overwrite an existing footer with the default.
  const isSaveDisabled = !organization?.id || isConfigLoading;

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    const { data: savedData } = await upsertOrganizationConfig({
      variables: { input: { emailFooterHtml: footerHtml } }
    });

    if (savedData?.upsertOrganizationConfig) {
      await refetch();
      setHasLocalEdits(false);
      showToast({ title: 'Email footer saved', type: ToastType.Success });
    }
  };

  const handlePreview = () => {
    openModal({
      title: 'Email footer preview',
      width: '640px',
      // Hide ModalV2's default action row so we can render Close *below* the
      // preview (afterActionsChildren renders under the default actions).
      primaryButtonProps: { sx: { display: 'none' } },
      secondaryButtonProps: { sx: { display: 'none' } },
      afterActionsChildren: (
        <>
          <Typography color="customGrey.dark">
            This is how your footer will appear at the bottom of emails.
          </Typography>
          <Box sx={previewFrame}>
            <HtmlIframeViewer
              srcDoc={footerHtml}
              title="Email footer preview"
              style={{ height: 300, display: 'block', backgroundColor: '#fff' }}
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" onClick={closeModal}>
              Close
            </Button>
          </Box>
        </>
      )
    });
  };

  return (
    <Box sx={container}>
      <Typography variant="h6">Custom email footer</Typography>
      <Typography sx={{ mb: 1 }} color="customGrey.dark">
        Add branded HTML shown at the bottom of your outbound emails.
      </Typography>
      <TextField
        multiline
        minRows={4}
        fullWidth
        sx={{ maxWidth: 700 }}
        value={footerHtml}
        onChange={(event) => {
          setHasLocalEdits(true);
          setFooterHtml(event.target.value);
        }}
        inputProps={{ 'aria-label': 'Email footer HTML' }}
      />
      <Box sx={actions}>
        <Button
          variant="primary"
          loading={loading}
          disabled={isSaveDisabled}
          onClick={handleSave}
        >
          Save
        </Button>
        <Button
          variant="secondary"
          startIcon={<Visibility />}
          onClick={handlePreview}
        >
          Preview
        </Button>
      </Box>
    </Box>
  );
};
